import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import BackgroundVideo from "@/components/BackgroundVideo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck } from "lucide-react";

/**
 * Team-wide invite landing — anyone with the URL can register as a player on this team.
 * Different from /invite/:token (which is a per-player pre-filled invite).
 */
export default function TeamInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser, fetchTeam } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [year, setYear] = useState("");
  const [position, setPosition] = useState("");
  const [dominantArm, setDominantArm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/team-invites/${token}`)
      .then((r) => {
        setInfo(r.data);
        const root = document.documentElement;
        if (r.data.team_primary) root.style.setProperty("--brand-primary", r.data.team_primary);
        if (r.data.team_secondary) root.style.setProperty("--brand-secondary", r.data.team_secondary);
      })
      .catch((e) => setErr(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Name is required"); return; }
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    setErr("");
    setBusy(true);
    try {
      const { data: u } = await api.post(`/team-invites/${token}/accept`, {
        name: name.trim(), email, password,
        year: year || undefined, position: position || undefined, dominant_arm: dominantArm || undefined,
      });
      setUser(u);
      await fetchTeam();
      nav("/portal", { replace: true });
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  if (err && !info) return (
    <div className="min-h-screen flex items-center justify-center text-white" style={{ backgroundColor: "var(--ink-1)" }}>
      <div className="rounded-xl ink-card p-8 max-w-md text-center" data-testid="invite-error">
        <h1 className="font-display font-black text-2xl text-white">Invite Link Invalid</h1>
        <p className="text-white/60 mt-3 text-sm">{err}</p>
        <p className="text-white/40 mt-4 text-xs">Ask your coach for a fresh team invite link.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen text-white relative" data-testid="team-invite-page">
      <BackgroundVideo overlayOpacity={0.7} />
      <div className="relative z-10 max-w-md mx-auto px-5 pt-10 pb-16">
        <div className="md:hidden mb-8"><Logo size="sm" variant="lockup" linkTo="/" dark /></div>
        <div className="hidden md:block mb-10"><Logo size="md" variant="lockup" linkTo="/" dark /></div>

        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Team Invite</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-1 text-white">
          Join {info?.team_name || "the team"}
        </h1>
        <p className="text-sm text-white/60 mt-2">
          Your coach shared this link. Create your account below — you'll go straight into the team's portal once you submit.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4 rounded-xl ink-card p-6">
          <div>
            <Label className="text-white/80">Your full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jake Miller" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="team-invite-name" />
          </div>
          <div>
            <Label className="text-white/80">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@school.edu" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="team-invite-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/80 text-xs">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="team-invite-password" />
            </div>
            <div>
              <Label className="text-white/80 text-xs">Confirm</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="team-invite-confirm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-white/80 text-xs">Year</Label>
              <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="Senior" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="team-invite-year" />
            </div>
            <div>
              <Label className="text-white/80 text-xs">Position</Label>
              <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="SP" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="team-invite-position" />
            </div>
            <div>
              <Label className="text-white/80 text-xs">Arm</Label>
              <Input value={dominantArm} onChange={(e) => setDominantArm(e.target.value)} placeholder="R or L" className="mt-1.5 bg-[var(--ink-1)] border-[var(--ink-border)] text-white" data-testid="team-invite-arm" />
            </div>
          </div>

          {err && <p className="text-sm text-red-400" data-testid="team-invite-error">{err}</p>}

          <Button type="submit" disabled={busy} className="w-full rounded-full text-white red-glow font-bold py-6" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="team-invite-submit">
            {busy ? "Creating account…" : <>Create my account <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>
        </form>

        <div className="flex items-center gap-2 text-white/40 text-xs mt-6">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span>Encrypted at rest. Your coach sees readiness only — never your raw HRV.</span>
        </div>
      </div>
    </div>
  );
}
